// steps/tabs/meter-readings/subtabs/AllReadingsSubTab.tsx
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "@shared/ui/badge";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { MeterReadingFilters } from "./Filters/AllReadingFilters";

interface MeterReadingFiltersType {
  veeStatus: string;
  status: string;
  meterType: string;
  service: string;
  page?: number;
  limit?: number;
}
interface AllReadingsSubTabProps {
  meterReadings?: any;
  filters: any;
  setFilters: any;
  currentPage: any;
  setCurrentPage: any;
}

const AllReadingsSubTab: React.FC<AllReadingsSubTabProps> = ({
  meterReadings,
  filters,
  setFilters,
  currentPage,
  setCurrentPage,
}) => {
  const { id, cycleId, action } = useParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [isListExpanded, setIsListExpanded] = useState(true);

  // Format filters for API hook

  // Define columns
  const columns: TableColumn<any>[] = [
    {
      key: "meterNo",
      header: "Resource ID",
    },
    {
      key: "consumerNumber",
      header: "Consumer",
      render: (value: string) => {
        return <span className="font-medium">{value || "N/A"}</span>;
      },
    },
    {
      key: "meterType",
      header: "Resource Type",
    },
    {
      key: "service",
      header: "Service",
    },
    {
      key: "lastReading",
      header: "Last Reading",
    },
    {
      key: "currentReading",
      header: "Current Reading",
    },
    {
      key: "consumption",
      header: "Consumption",
    },
    {
      key: "veeStatus",
      header: "VEE Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "Pass":
              return "default";
            case "Adjusted":
              return "secondary";
            case "Estimated":
              return "secondary";
            default:
              return "outline";
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "Pass":
              return "bg-green-100 text-green-700";
            case "Adjusted":
              return "bg-yellow-100 text-yellow-700";
            case "Estimated":
              return "bg-blue-100 text-blue-700";
            default:
              return "";
          }
        },
      },
    },
    {
      key: "status",
      header: "Status",
      render: (value: any) => (
        <Badge className={getStatusBadgeClass(value)}>{value || "NA"}</Badge>
      ),
    },
  ];

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setFilters((prev: any) => ({ ...prev, search: searchValue }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFiltersChange = (newFilters: MeterReadingFiltersType) => {
    setFilters((prev: any) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      veeStatus: "all",
      status: "all",
      meterType: "all",
      service: "all",
      search: "",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Calculate pagination
  const totalItems = meterReadings?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className="space-y-4">
      {/* Meter Reading List */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Header */}
        <div
          className="p-4 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => setIsListExpanded(!isListExpanded)}
        >
          <div>
            <h3 className="font-semibold text-base">Reading List</h3>
            <p className="text-sm text-gray-600">
              All meters in the current billing cycle
            </p>
          </div>
        </div>

        <div className="p-4">
          <GenericTable
            data={meterReadings?.results || []}
            columns={columns}
            rowKey="meterNo"
            searchPlaceholder="Search by meter ID, account, or consumer name"
            searchParamName="search"
            searchDebounceMs={300}
            onSearch={handleSearch}
            filterPanel={
              <MeterReadingFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApplyFilters={handleFiltersChange}
                onClearFilters={clearFilters}
              />
            }
            pagination={{
              currentPage,
              totalPages,
              totalItems,
              itemsPerPage: PAGE_SIZE,
              startItem,
              endItem,
            }}
            onPageChange={handlePageChange}
            emptyMessage="No  readings found"
          />
        </div>
      </div>
    </div>
  );
};

export default AllReadingsSubTab;
